import React from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { AnimatePresence, motion } from 'framer-motion';
import { FiCheck, FiChevronDown, FiChevronRight, FiX } from 'react-icons/fi';
import { useGetGigs } from '../api/queries';
import { useStripeAccountStatus } from '../api/stripe';
import { useAuth } from '../hooks/useAuth';
import { usePrefersReducedMotion } from '../hooks/useMediaQuery';
import { useUserStore } from '../store/useUserStore';
import { cn } from '../utils/cn';
import {
  buildChecklist,
  checklistProgress,
  CHECKLIST_ROUTES,
  type ChecklistItemId,
} from './checklist';
import { checklistCopy } from './copy';
import { TOUR, tourTarget } from './targets';
import { matchRoute } from './tourLogic';
import { useOnboarding } from './useOnboardingStore';
import { useTour } from './useTour';

const ITEM_LINKS: Record<ChecklistItemId, string> = {
  stripe: '/profile/settings/payments',
  gig: '/gigs/create',
};

export const GettingStarted: React.FC = () => {
  const isAuthenticated = useAuth().isAuthenticated;
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { flags } = useOnboarding();
  const { isActive } = useTour();

  if (!isAuthenticated || flags.checklistDismissed || isActive) return null;
  if (!CHECKLIST_ROUTES.some((route) => matchRoute(route, pathname))) return null;
  return <ChecklistDock />;
};

const ChecklistDock: React.FC = () => {
  const user = useUserStore((s) => s.user);
  const { flags, setChecklist } = useOnboarding();
  const reducedMotion = usePrefersReducedMotion();

  const stripe = useStripeAccountStatus();
  const gigs = useGetGigs({ user: user?.id, page_size: 1 }, { enabled: !!user?.id });

  const items = buildChecklist({
    stripeReady: stripe.isError
      ? false
      : stripe.data
        ? Boolean(stripe.data.onboarding_completed || stripe.data.can_receive_payments)
        : undefined,
    postedGig: gigs.isError ? false : gigs.data ? gigs.data.count > 0 : undefined,
  });
  const progress = checklistProgress(items);
  if (progress.complete) return null;

  const collapsed = flags.checklistCollapsed;
  const percent = Math.round((progress.done / progress.total) * 100);
  const transition = reducedMotion ? { duration: 0 } : { duration: 0.18 };

  return (
    <div
      {...tourTarget(TOUR.checklist)}
      className="fixed bottom-28 right-4 z-40 md:bottom-6 md:right-6"
    >
      <AnimatePresence mode="wait" initial={false}>
        {collapsed ? (
          <motion.button
            key="pill"
            type="button"
            onClick={() => setChecklist({ checklistCollapsed: false })}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={transition}
            aria-label={`${checklistCopy.collapsedLabel}: ${progress.done} of ${progress.total} done. Open checklist`}
            className="flex items-center gap-2.5 rounded-full border border-white/10 bg-[#161618] py-2 pl-2 pr-4 text-[13px] font-medium text-white shadow-[0_12px_32px_rgba(0,0,0,0.55)] transition-colors hover:border-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
          >
            <ProgressRing percent={percent} />
            {checklistCopy.collapsedLabel}
            <span className="text-zinc-500">
              {progress.done}/{progress.total}
            </span>
          </motion.button>
        ) : (
          <motion.section
            key="card"
            aria-labelledby="getting-started-title"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
            transition={transition}
            className="w-[min(340px,calc(100vw-32px))] rounded-2xl border border-white/10 bg-[#161618] p-4 shadow-[0_18px_48px_rgba(0,0,0,0.6)]"
          >
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <h2 id="getting-started-title" className="text-[15px] font-semibold text-white">
                  {checklistCopy.title}
                </h2>
                <p className="mt-0.5 text-[12px] text-zinc-500">
                  {progress.done} of {progress.total} done
                </p>
              </div>
              <button
                type="button"
                onClick={() => setChecklist({ checklistCollapsed: true })}
                aria-label="Minimise checklist"
                className="rounded-full p-1 text-zinc-500 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
              >
                <FiChevronDown size={16} aria-hidden />
              </button>
              <button
                type="button"
                onClick={() => setChecklist({ checklistDismissed: true })}
                aria-label={checklistCopy.hide}
                className="rounded-full p-1 text-zinc-500 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
              >
                <FiX size={16} aria-hidden />
              </button>
            </div>

            <div
              className="mt-3 h-1.5 overflow-hidden rounded-full bg-white/10"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={progress.total}
              aria-valuenow={progress.done}
              aria-label="Checklist progress"
            >
              <div
                className="h-full rounded-full bg-[#22c55e] transition-[width] duration-500"
                style={{ width: `${percent}%` }}
              />
            </div>

            <ul className="mt-2 space-y-0.5">
              {items.map((item) => {
                const copy = checklistCopy.items[item.id];
                return (
                  <li key={item.id} className="group relative">
                    <Link
                      to={ITEM_LINKS[item.id]}
                      className="flex items-start gap-3 rounded-xl px-2 py-2 transition-colors hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
                    >
                      <span
                        className={cn(
                          'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border',
                          item.done
                            ? 'border-[#22c55e] bg-[#22c55e] text-black'
                            : 'border-white/20 text-transparent',
                          item.pending && !item.done && 'animate-pulse',
                        )}
                        aria-hidden
                      >
                        <FiCheck size={12} strokeWidth={3} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span
                          className={cn(
                            'block text-[13px] font-medium',
                            item.done ? 'text-zinc-500 line-through' : 'text-white',
                          )}
                        >
                          {copy.label}
                          <span className="sr-only">{item.done ? ' (done)' : ''}</span>
                        </span>
                        {!item.done && (
                          <span className="mt-0.5 block text-[12px] leading-snug text-zinc-500">
                            {copy.hint}
                          </span>
                        )}
                      </span>
                      {!item.done && (
                        <FiChevronRight
                          size={14}
                          className="mt-1 shrink-0 text-zinc-600 transition-transform group-hover:translate-x-0.5"
                          aria-hidden
                        />
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </motion.section>
        )}
      </AnimatePresence>
    </div>
  );
};

const ProgressRing: React.FC<{ percent: number }> = ({ percent }) => {
  const radius = 9;
  const circumference = 2 * Math.PI * radius;
  return (
    <svg width={22} height={22} viewBox="0 0 22 22" aria-hidden className="-rotate-90">
      <circle
        cx={11}
        cy={11}
        r={radius}
        fill="none"
        stroke="rgba(255,255,255,0.12)"
        strokeWidth={2.5}
      />
      <circle
        cx={11}
        cy={11}
        r={radius}
        fill="none"
        stroke="#22c55e"
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={circumference * (1 - percent / 100)}
      />
    </svg>
  );
};
